import type { AgentComponent, DecisionPreset, WorkflowGraph, WorkflowStep } from '@vagusx/clients';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  addBranch,
  defaultExits,
  endingExits,
  nextOf,
  otherwiseLabel,
  setOtherwise,
  stepExits,
} from './graph';

/**
 * An arrow dragged from one step to another, on a step that can end more than
 * one way: which ending should go there? One of the endings not yet sent
 * anywhere of its own, or all of them — which replaces the usual next step.
 */

export interface Connection {
  from: string;
  to: string;
}

export function ConnectDialog({
  connection,
  graph,
  steps,
  components,
  presets,
  onClose,
  onConnect,
}: {
  connection: Connection | null;
  graph: WorkflowGraph;
  steps: ReadonlyMap<string, WorkflowStep>;
  components: ReadonlyMap<string, AgentComponent>;
  presets: readonly DecisionPreset[];
  onClose: () => void;
  onConnect: (graph: WorkflowGraph) => void;
}): React.ReactElement {
  const node = connection && graph.nodes.find((n) => n.id === connection.from);
  const targetNode = connection && graph.nodes.find((n) => n.id === connection.to);
  const step = node ? steps.get(node.stepId) : undefined;
  const exits = stepExits(step, components.get(step?.componentKey ?? ''), presets);
  const rest = node ? defaultExits(node, exits) : [];
  // A step that ends the call on purpose on some endings takes no default.
  const endsOnPurpose = node ? endingExits(node, exits).length > 0 : false;
  const current = node ? nextOf(node).otherwise : null;
  const currentNode = current ? graph.nodes.find((n) => n.id === current) : undefined;
  const name = (stepId: string | undefined): string =>
    (stepId && steps.get(stepId)?.name) || 'that step';

  return (
    <Dialog open={connection !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>When should the call go to “{name(targetNode?.stepId)}”?</DialogTitle>
          <DialogDescription>After “{name(node?.stepId)}”.</DialogDescription>
        </DialogHeader>
        {connection && node && (
          <div className="space-y-2">
            {/* "Only when" needs a default for the other endings to take; on a
                step with none, this arrow becomes the default. */}
            {(current !== null || endsOnPurpose) &&
              rest.map((e) => (
                <Choice
                  key={e.id}
                  title={`Only when: ${e.label}`}
                  onClick={() => onConnect(addBranch(graph, node.id, e.id, connection.to))}
                />
              ))}
            {!endsOnPurpose && (
              <Choice
                title={
                  node.transitions.some((t) => t.when) ? otherwiseLabel(node, exits) : 'Every time'
                }
                body={
                  currentNode
                    ? `Instead of “${name(currentNode.stepId)}”`
                    : 'This becomes the next step'
                }
                onClick={() => onConnect(setOtherwise(graph, node.id, connection.to))}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Choice({
  title,
  body,
  onClick,
}: {
  title: string;
  body?: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full flex-col items-start gap-0.5 rounded-md border p-3 text-left hover:border-primary hover:bg-accent"
    >
      <span className="text-sm font-medium">{title}</span>
      {body && <span className="text-xs text-muted-foreground">{body}</span>}
    </button>
  );
}
